import { Expression } from "../../Expressions/Expression";
import { PartialNullable } from "../../Extensions";
import { ObjectHelper } from "../../Helpers/ObjectHelper";
import { StringHelper } from "../../Helpers/StringHelper";
import { IJNativeEvent, JNative, JNativeStatic } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyString } from "../../RadObject";
import { WebApiClientService } from "../../Server/WebApiClientService";
import { ServiceManager } from "../../ServiceManager";
import { DialogResultEnum } from "../Dialogs/DialogResultEnum";
import { IEntityPickerDialog } from "../Dialogs/EntityPickerDialog";
import { Button } from "./Button";
import { IInputControl, InputControl, InputControlPropertyValues } from "./InputControl";
import { Panel } from "./Panel";
import { IPropertyEditor } from "./PropertyEditor";
import { NAMESPACE } from "./_Namespace";

import "./EntityPicker.scss";

export type EntityPickerPropertyValues =
    InputControlPropertyValues
    & {
        addNew: boolean;
        entityDescriptionProperty: string;
        entityFilter: Expression | string;
        entityName: string;
        entityNameFriendly: string;
        entitySetName: string;
        entitySetNameFriendly: string;
        textBoxSearchTitle: string;
    };

export type EntityPickerProperties = {
    [name in keyof EntityPickerPropertyValues]: Property<EntityPickerPropertyValues[name]>
};

export interface IEntityPicker extends IInputControl, EntityPickerProperties
{
}

export class EntityPicker extends InputControl implements IEntityPicker
{
    public static override classNamespace = NAMESPACE + "EntityPicker";
    public static override className = "EntityPicker";

    // #region Global Variables

    protected $$descriptionElement?: JNative;
    protected buttonClear?: Button;
    protected buttonSearch?: Button;
    protected dialog?: IEntityPickerDialog;
    protected wrapper?: Panel;

    // #endregion

    // #region Properties

    public addNew!: Property<boolean>;
    public entityDescriptionProperty!: PropertyString;
    public entityFilter!: Property<Expression | string>;
    public entityName!: PropertyString;
    public entityNameFriendly!: PropertyString;
    public entitySetName!: PropertyString;
    public entitySetNameFriendly!: PropertyString;
    public textBoxSearchTitle!: PropertyString;

    // #endregion

    // #region Constructors

    constructor(propertyValues?: PartialNullable<EntityPickerPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-entity-picker");

        // Trigger Property Changes
        this.addNew.triggerChange();
        this.entityNameFriendly.triggerChange();
    }

    // #endregion

    // #region Protected Methods

    protected _addNewChanged(change: PropertyChange<boolean>)
    {
        this.dialog?.addNew.set(change.currentValue);
    }
    protected _entityNameFriendlyChanged(change: PropertyChange<string>)
    {
        this.buttonSearch?.elementTitle.set(change.currentValue ? `Search for a ${change.currentValue}` : null);
        this.buttonClear?.elementTitle.set(change.currentValue ? `Clear selected ${change.currentValue}` : null);
    }
    protected async _openDialogAsync()
    {
        return await this.dialog?.openAsync();
    }
    protected async _searchAsync()
    {
        if (!this.dialog)
        {
            this.dialog = new (await import("../Dialogs/EntityPickerDialog")).EntityPickerDialog({
                addNew: this.addNew.get(),
                columns: [
                    { dataSourceProperty: this.entityDescriptionProperty.get(), defaultSort: true, name: "Description", sortable: true, },
                    {
                        addCellContents: ($$cell: JNative, column: any, dataSource: any, value: any) =>
                        {
                            new Button({ cssClass: "rad-action-select", inline: true, text: "Select", elementTitle: value ? `Select ${value}` : null })
                                .$$element.get()?.appendTo($$cell);
                        },
                        cssClass: "rad-column-item-select",
                        dataSourceProperty: this.entityDescriptionProperty.get(),
                    },
                ],
                entityDescriptionProperty: this.entityDescriptionProperty.get(),
                entityNamePlural: this.entitySetName.get(),
                title: `Search ${this.entitySetNameFriendly.get()}`,
            });
        }

        this.dialog.textBoxSearchTitle.set(this.textBoxSearchTitle.get());

        let
            entityFilter = this.entityFilter.get();

        if (typeof entityFilter === "string")
        {
            // TODO: Make Property Editor -> Editor Control bind to the datasource?
            const
                propertyEditor = this.$$element.get()?.closest(".rad-control-property-editor").radControl() as IPropertyEditor | undefined,
                data = propertyEditor?.dataSource.get()?.data.get();

            entityFilter = entityFilter.replace(/\[([^\]]+)\]/g, (match, p1) =>
            {
                const
                    value = data ? data[p1] : null;

                if (ObjectHelper.isNullOrUndefined(value))
                    return "null";

                return value.toString();
            });
        }

        this.dialog.entityFilter.set(entityFilter);

        const
            response = await this._openDialogAsync();

        if (response?.dialogResult === DialogResultEnum.ok)
        {
            this.value.set(response.promiseData);

            this._loadingEnqueue()

            try { await this._updateTextAsync(); }
            finally { this._loadingDequeue(); }
        }
    }
    protected async _updateTextAsync()
    {
        const
            value = this.value.get(),
            entitySetName = this.entitySetName.get(),
            entityDescriptionProperty = this.entityDescriptionProperty.get();

        if (!value || !entitySetName || !entityDescriptionProperty)
        {
            this.text.set(null);
            return;
        }

        const
            results =
                await ServiceManager.getRequired(WebApiClientService)
                    .createEntityGetRequest(entitySetName, value, ["id", entityDescriptionProperty], { "RAD-Option-Include-Soft-Deleted": true }, false)
                    .executeAsync();

        if (results.deletedGuid)
            this.addCssClass("rad-state-deleted");
        else
            this.removeCssClass("rad-state-deleted");

        this.text.set(results ? results[entityDescriptionProperty] : null);
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: EntityPickerPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.addNew = new Property<boolean>(this, "System.Boolean, System.Private.CoreLib", propertyValues.addNew, this._addNewChanged);
        this.entityDescriptionProperty = new PropertyString(this, propertyValues.entityDescriptionProperty);
        this.entityFilter = new Property<Expression | string>(this, "System.Object, System.Private.CoreLib", propertyValues.entityFilter);
        this.entityName = new PropertyString(this, propertyValues.entityName);
        this.entityNameFriendly = new PropertyString(this, propertyValues.entityNameFriendly, this._entityNameFriendlyChanged);
        this.entitySetName = new PropertyString(this, propertyValues.entitySetName);
        this.entitySetNameFriendly = new PropertyString(this, propertyValues.entitySetNameFriendly);
        this.textBoxSearchTitle = new PropertyString(this, propertyValues.textBoxSearchTitle);
    }

    protected override _bindEvents()
    {
        super._bindEvents();

        this.buttonClear?.on("click", { self: this }, this.onButtonClearClicked);
        this.buttonSearch?.on("click", { self: this }, this.onButtonSearchClicked);
        this.wrapper?.on("click", { self: this }, this.onWrapperClicked);
    }
    protected override _createElement()
    {
        const
            $$element = super._createElement();

        this.wrapper =
            new Panel({ cssClass: "rad-wrapper" })
                .appendTo($$element);

        this.$$descriptionElement =
            JNativeStatic.create$$({
                nodeName: "p",
                attributes: { "class": "rad-description" }
            });

        this.wrapper.append(this.$$descriptionElement);

        this.buttonSearch =
            new Button({ cssClass: "rad-action-search", inline: true, svgKey: "icon-search" });

        this.buttonSearch?.$$element.get()?.appendTo($$element);

        this.buttonClear =
            new Button({ cssClass: "rad-action-clear", inline: true, svgKey: "icon-cross" });

        this.buttonClear?.$$element.get()?.appendTo($$element);

        return $$element;
    }
    protected override _readOnlyChanged(change: PropertyChange<boolean>)
    {
        this.buttonSearch?.enabled.set(!change.currentValue);
    }
    protected override _refreshFromDataSource()
    {
        super._refreshFromDataSource()
        this._updateTextAsync();
    }
    protected override _textChanged(change: PropertyChange<string>)
    {
        super._textChanged(change);
        this.$$descriptionElement?.text(change.currentValue);
        this.$$descriptionElement?.attr("title", change.currentValue ?? StringHelper.empty);
    }

    // #endregion

    // #region Event Handlers

    protected onButtonClearClicked(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self.value.set(null);
    }
    protected onButtonSearchClicked(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self._searchAsync();
    }
    protected onWrapperClicked(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self._searchAsync();
    }

    // #endregion
}

